import { alpha, Theme } from '@mui/material/styles'

export type StickyCell = { side: 'left' | 'right'; offset: number; hasSeam: boolean }

export type StickyColumns = {
    index?: StickyCell
    firstColumn?: StickyCell
    actions?: StickyCell
}

type StickyLayout = {
    isEnabled: boolean
    showIndexColumn: boolean
    indexColumnWidth: number
    hasActionsColumn: boolean
    /** Off where the screen is too narrow to give a whole column away to pinning. */
    pinsFirstColumn: boolean
    hasLeftOverflow: boolean
    hasRightOverflow: boolean
}

const SEAM_SPREAD = {
    left: '4px 0 8px -4px',
    right: '-4px 0 8px -4px',
}

/** tier1 is the ink of the mode it is in, so the seam darkens a light table and lightens a dark one. */
const getSeamShadow = (theme: Theme, side: StickyCell['side']) =>
    `${SEAM_SPREAD[side]} ${alpha(theme.palette.figma.grayscale.stroke, 0.2)}`

export const getStickyColumns = ({
    isEnabled,
    showIndexColumn,
    indexColumnWidth,
    hasActionsColumn,
    pinsFirstColumn,
    hasLeftOverflow,
    hasRightOverflow,
}: StickyLayout): StickyColumns => {
    if (!isEnabled) return {}

    return {
        // The index sits behind the first column, so the seam belongs to the first column alone,
        // and pinning the index without it would hold back a row number and nothing else.
        index: showIndexColumn && pinsFirstColumn ? { side: 'left', offset: 0, hasSeam: false } : undefined,
        firstColumn: pinsFirstColumn
            ? { side: 'left', offset: showIndexColumn ? indexColumnWidth : 0, hasSeam: hasLeftOverflow }
            : undefined,
        actions: hasActionsColumn ? { side: 'right', offset: 0, hasSeam: hasRightOverflow } : undefined,
    }
}

/** A header names its column rather than covering the rows sliding under it, so it draws no seam. */
export const withoutSeam = (sticky?: StickyCell): StickyCell | undefined => sticky && { ...sticky, hasSeam: false }

/** A pinned cell paints over the ones scrolling under it, so it carries its row's own background. */
export const getStickyCellSx = (sticky: StickyCell | undefined, background: string) =>
    !!sticky &&
    ((theme: Theme) => ({
        position: 'sticky' as const,
        [sticky.side]: sticky.offset,
        zIndex: 1,
        bgcolor: background,
        ...(sticky.hasSeam && { boxShadow: getSeamShadow(theme, sticky.side) }),
    }))
